import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { School, ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { title: "Register interest", body: "Complete the enquiry form or call us. We'll arrange a short discovery conversation." },
  { title: "Plan the visit", body: "We agree dates, numbers, space required and the schedule for the day." },
  { title: "Consent packs sent", body: "Simple information and consent forms are sent home for families." },
  { title: "Visit day", body: "We arrive, set up, and see students in short, structured appointments." },
  { title: "Reports & follow-up", body: "Every family receives a plain-English report with any next steps." },
];

const REQUIREMENTS = [
  "Level ground for the van (approx. car park size)",
  "Access to standard 240V power (we bring extension leads)",
  "A staff contact for the visit day",
  "A quiet indoor waiting area is helpful, but not essential",
];

const initialState = {
  school_name: "",
  contact_person: "",
  position: "",
  email: "",
  phone: "",
  school_address: "",
  number_of_students: "",
  preferred_visit_period: "",
  message: "",
  consent: false,
};

const ForSchools = () => {
  if (typeof document !== "undefined") document.title = "For Schools — School Smiles Australia";
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.school_name.trim()) e.school_name = "Required";
    if (!form.contact_person.trim()) e.contact_person = "Required";
    if (!form.position.trim()) e.position = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.school_address.trim()) e.school_address = "Required";
    if (!form.number_of_students.trim()) e.number_of_students = "Required";
    if (!form.preferred_visit_period) e.preferred_visit_period = "Please select";
    if (!form.consent) e.consent = "Consent is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries/school`, form);
      toast.success("Thanks! Your enquiry has been received. We'll be in touch shortly.");
      setForm(initialState);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="container-page pt-16 md:pt-24 pb-8" data-testid="schools-hero">
        <div className="max-w-3xl">
          <span className="eyebrow"><School size={14} strokeWidth={2.5}/> For schools</span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">
            Bring dental care to your students, without the disruption
          </h1>
          <p className="mt-5 text-[#4B6B86] text-lg leading-relaxed">
            We work with primary and secondary schools across Australia. Here's how it works, what we need from you and how to register your interest.
          </p>
        </div>
      </section>

      <section className="container-page py-12" data-testid="schools-process">
        <SectionHeading eyebrow="How it works" title="A simple, well-communicated process"/>
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-6">
          {STEPS.map((s, i) => (
            <li key={s.title} className="card-soft p-6" data-testid={`schools-step-${i+1}`}>
              <div className="text-3xl font-display font-extrabold text-[#1677C8]/25">0{i+1}</div>
              <h3 className="mt-2 font-display font-semibold text-[#14324A]">{s.title}</h3>
              <p className="mt-2 text-sm text-[#4B6B86] leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="container-page py-12 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="schools-requirements">
        <div className="card-soft p-10">
          <h2 className="font-display font-bold text-2xl text-[#14324A]">What we need from your school</h2>
          <ul className="mt-4 space-y-2 text-[#4B6B86]">
            {REQUIREMENTS.map((r) => (
              <li key={r} className="flex gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-[#1677C8]" aria-hidden="true"/>{r}
              </li>
            ))}
          </ul>
        </div>
        <div className="card-soft p-10 bg-[#F4F9FF]">
          <h2 className="font-display font-bold text-2xl text-[#14324A]">On the visit day</h2>
          <p className="mt-3 text-[#4B6B86] leading-relaxed">
            Our team arrives before the school day begins, sets up quickly and works from a scheduling sheet agreed with your team. Students are called individually, and returned to class as soon as their appointment ends. We provide reports and follow-up recommendations to families within a few working days.
          </p>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquiry" className="container-page py-16 scroll-mt-24" data-testid="schools-form-section">
        <div className="card-soft p-8 md:p-12">
          <SectionHeading eyebrow="Register interest" title="School enquiry form" description="Complete the form below and we'll be in touch within two working days."/>
          <form className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" onSubmit={onSubmit} noValidate data-testid="schools-enquiry-form">
            <Field label="School name" error={errors.school_name}>
              <Input value={form.school_name} onChange={(e)=>setField("school_name", e.target.value)} data-testid="input-school-name" placeholder="e.g. Sunshine Primary School"/>
            </Field>
            <Field label="Contact person" error={errors.contact_person}>
              <Input value={form.contact_person} onChange={(e)=>setField("contact_person", e.target.value)} data-testid="input-contact-person"/>
            </Field>
            <Field label="Position" error={errors.position}>
              <Input value={form.position} onChange={(e)=>setField("position", e.target.value)} data-testid="input-position" placeholder="e.g. Deputy Principal"/>
            </Field>
            <Field label="Email" error={errors.email}>
              <Input type="email" value={form.email} onChange={(e)=>setField("email", e.target.value)} data-testid="input-email"/>
            </Field>
            <Field label="Phone" error={errors.phone}>
              <Input value={form.phone} onChange={(e)=>setField("phone", e.target.value)} data-testid="input-phone"/>
            </Field>
            <Field label="Number of students" error={errors.number_of_students}>
              <Input value={form.number_of_students} onChange={(e)=>setField("number_of_students", e.target.value)} data-testid="input-num-students" placeholder="Approximate total"/>
            </Field>
            <Field label="School address" error={errors.school_address} full>
              <Input value={form.school_address} onChange={(e)=>setField("school_address", e.target.value)} data-testid="input-school-address"/>
            </Field>
            <Field label="Preferred visit period" error={errors.preferred_visit_period}>
              <Select value={form.preferred_visit_period} onValueChange={(v)=>setField("preferred_visit_period", v)}>
                <SelectTrigger data-testid="select-preferred-period"><SelectValue placeholder="Select a period"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="term-1">Term 1</SelectItem>
                  <SelectItem value="term-2">Term 2</SelectItem>
                  <SelectItem value="term-3">Term 3</SelectItem>
                  <SelectItem value="term-4">Term 4</SelectItem>
                  <SelectItem value="flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-[#14324A]">Message</Label>
              <Textarea rows={5} value={form.message} onChange={(e)=>setField("message", e.target.value)} data-testid="input-message" placeholder="Anything else we should know?" className="mt-2"/>
            </div>
            <div className="md:col-span-2 flex items-start gap-3">
              <Checkbox id="consent" checked={form.consent} onCheckedChange={(v)=>setField("consent", !!v)} data-testid="checkbox-consent"/>
              <label htmlFor="consent" className="text-sm text-[#4B6B86] leading-relaxed">
                I consent to School Smiles Australia using the information above to respond to this enquiry, in line with the <a href="/privacy-policy" className="text-[#1677C8] underline">Privacy Policy</a>.
              </label>
            </div>
            {errors.consent && <p className="md:col-span-2 text-sm text-red-600 -mt-2">{errors.consent}</p>}
            <div className="md:col-span-2">
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-schools-form">
                {submitting ? "Sending..." : "Submit enquiry"}
                <ArrowRight size={18} strokeWidth={2.5}/>
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

const Field = ({ label, error, full, children }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-sm font-medium text-[#14324A]">{label}</Label>
    <div className="mt-2">{children}</div>
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

export default ForSchools;
